import React, { useEffect, useRef, useState } from "react";
import { Paperclip, X, FileText, Film, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fileUrl } from "./MessageExtras";

export const MAX_FILES = 5;
const MAX_MB = 8;
const ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.pdf,.txt,.md,.csv,.log,.json,.mp4,.webm,.mov";
export const CATEGORY_ICON = { billing: "credit-card", technical: "wrench", account: "shield", feature: "lightbulb", other: "message-circle" };
export const STATUS_STYLE = {
  open: "bg-sky-100 text-sky-700", assigned: "bg-indigo-100 text-indigo-700", in_progress: "bg-amber-100 text-amber-700", escalated: "bg-fuchsia-100 text-fuchsia-700",
  waiting_user: "bg-orange-100 text-orange-700", resolved: "bg-emerald-100 text-emerald-700", closed: "bg-slate-200 text-slate-600",
};
export const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
export const fmtSize = (n) => n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export function StatusBadge({ status, label }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`} data-testid={`ticket-status-${status}`}>{label}</span>;
}

// Thumbnail/chip for an uploaded attachment (ticket body & replies).
export function AttachmentChip({ a, onRemove }) {
  const Icon = a.kind === "video" ? Film : a.kind === "image" ? ImageIcon : FileText;
  const open = () => window.open(fileUrl(a.path), "_blank", "noopener");
  return (
    <span className="group inline-flex max-w-full items-center gap-1.5 rounded-xl border border-[#E6EAF2] bg-white py-1 pl-1 pr-2 text-[11px] text-slate-700 shadow-sm" data-testid="ticket-attachment" data-no-i18n="true">
      {a.kind === "image" ? <img src={fileUrl(a.path)} alt="" className="h-7 w-7 cursor-pointer rounded-lg object-cover" onClick={open} /> : <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#EEF3FF] text-[#2F6BFF]"><Icon size={13} /></span>}
      <button type="button" onClick={open} className="max-w-[150px] truncate text-left font-semibold hover:underline">{a.name}</button>
      <span className="text-slate-400">{fmtSize(a.size)}</span>
      {onRemove && <button type="button" onClick={onRemove} className="ml-0.5 text-slate-400 hover:text-red-500" data-testid="ticket-attachment-remove"><X size={12} /></button>}
    </span>
  );
}

// Multi-file uploader (max 5 × 8 MB) → POST {endpoint} per file; calls onChange(list) with server refs.
export function AttachmentUploader({ value = [], onChange, endpoint = "/support/upload", compact = false, testid = "ticket-upload" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);
  const [drag, setDrag] = useState(false);
  const latest = useRef(value); useEffect(() => { latest.current = value; }, [value]);
  const addFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (latest.current.length + list.length > MAX_FILES) { toast.error(`Maksimal ${MAX_FILES} lampiran per pesan`); return; }
    for (const f of list) {
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`«${f.name}» lebih dari ${MAX_MB} MB`); continue; }
      setBusy((n) => n + 1);
      try {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange([...latest.current, r.data]); latest.current = [...latest.current, r.data];
      } catch (e) { toast.error(e?.response?.data?.detail || `Gagal mengunggah «${f.name}»`); }
      finally { setBusy((n) => n - 1); }
    }
  };
  return (
    <div data-testid={testid}>
      {value.length > 0 && <div className="mb-2 flex flex-wrap gap-1.5">{value.map((a) => <AttachmentChip key={a.path} a={a} onRemove={() => onChange(value.filter((x) => x.path !== a.path))} />)}</div>}
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        onClick={() => ref.current?.click()} role="button" tabIndex={0}
        className={`flex cursor-pointer items-center gap-2 rounded-xl border border-dashed px-3 transition ${compact ? "py-1.5" : "py-3"} ${drag ? "border-[#2F6BFF] bg-[#EEF3FF]" : "border-[#CBD5E1] bg-white hover:border-[#2F6BFF]/60 hover:bg-[#F7F9FF]"}`} data-testid={`${testid}-dropzone`}>
        {busy ? <Loader2 size={15} className="animate-spin text-[#2F6BFF]" /> : <Paperclip size={15} className="text-[#2F6BFF]" />}
        <span className="text-xs text-slate-600">{busy ? "Mengunggah…" : compact ? "Lampirkan berkas" : "Seret berkas ke sini atau klik untuk memilih"}</span>
        {!compact && <span className="ml-auto hidden text-[10px] text-slate-400 sm:inline">Maks {MAX_FILES} berkas × {MAX_MB} MB · gambar, PDF, log/teks, video pendek</span>}
        <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
      </div>
    </div>
  );
}
